import {
  BellSimpleIcon,
  CaretRightIcon,
  PlayIcon,
  PlusIcon,
  SignOutIcon,
  UserCircleIcon,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

import { cn } from '@shared/lib/utils';
import { Heading } from '@shared/ui';
import { Avatar, AvatarFallback } from '@shared/ui/avatar';
import { Button, buttonVariants } from '@shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/ui/dropdown-menu';
import { SidebarTrigger } from '@shared/ui/sidebar';

interface AppHeaderProps {
  breadcrumbs?: { label: string; href?: string }[];
  primaryAction?: {
    label: string;
    icon?: 'new' | 'start';
    onClick?: () => void;
  };
}

export function AppHeader({ breadcrumbs, primaryAction }: AppHeaderProps) {
  const ActionIcon = primaryAction?.icon === 'start' ? PlayIcon : PlusIcon;
  const actionLabel = primaryAction?.label ?? 'New Practice';

  return (
    <header
      className="
        flex h-15 w-full min-w-0 shrink-0 items-center gap-2 rounded-full bg-background-0 py-2.5
        shadow-sm ring-1 ring-border/70
      "
    >
      <div
        className="
          flex w-full min-w-0 items-center gap-1 px-2.5
          lg:gap-2
        "
      >
        <SidebarTrigger className="size-10 rounded-full" />

        <div className="min-w-0 flex-1">
          {breadcrumbs && breadcrumbs.length > 0 ? (
            <div
              className="
                flex min-w-0 items-center gap-1.5 overflow-hidden text-sm text-muted-foreground
              "
            >
              {breadcrumbs.map((crumb, index) => (
                <div key={crumb.href ?? crumb.label} className="flex min-w-0 items-center gap-1.5">
                  {index > 0 && <CaretRightIcon className="size-3.5 shrink-0" />}
                  {crumb.href && index < breadcrumbs.length - 1 ? (
                    <Link
                      to={crumb.href}
                      className="
                        truncate outline-hidden transition-colors
                        hover:text-foreground
                        focus-visible:text-foreground
                      "
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span
                      className={cn(
                        'truncate',
                        index === breadcrumbs.length - 1 && 'font-medium text-foreground'
                      )}
                    >
                      {crumb.label}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <Heading tag="h1">Welcome back, Username</Heading>
          )}
        </div>

        <div className="ml-auto flex shrink-0 items-center justify-start gap-2">
          <Button
            variant="outline"
            size="lg"
            className="
              hidden
              sm:flex
            "
            onClick={primaryAction?.onClick}
          >
            <ActionIcon data-icon="inline-start" />
            {actionLabel}
          </Button>
          <Button
            variant="ghost"
            size="icon-lg"
            className="
              hidden size-10
              sm:flex
            "
          >
            <BellSimpleIcon />
          </Button>

          <DropdownMenu modal={false}>
            <DropdownMenuTrigger
              openOnHover
              delay={0}
              closeDelay={100}
              nativeButton={false}
              render={
                <Link
                  to="/profile"
                  className={cn(
                    buttonVariants({ variant: 'outline', size: 'icon-lg' }),
                    'size-10 rounded-full'
                  )}
                  aria-label="Open profile menu"
                />
              }
            >
              <Avatar>
                <AvatarFallback className="bg-primary font-medium text-white uppercase">
                  OK
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent sideOffset={20}>
              <DropdownMenuGroup>
                <DropdownMenuItem render={<Link to="/profile" />}>
                  <UserCircleIcon weight="bold" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  render={<Link to="/" />}
                  className="
                    text-destructive
                    data-highlighted:bg-destructive/10 data-highlighted:text-destructive
                    [&_svg]:text-current
                  "
                >
                  <SignOutIcon weight="bold" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
